import { clearFeedbackFromLocalStorage } from './coachingFeedback'

export const state = () => ({
  available: null,
  hash: null,
  coachingDiagnosis: [],
})

export const getters = {
  activeAllowance: state => code =>
    state.available?.activeAllowanceClients?.find(item => item.code === code),
  activeAllowanceSalesman: state => code =>
    state.available?.activeAllowanceSalesmen?.includes(code),
  isUpdated(state) {
    return !!state.available
  },
  indexed(state) {
    const { available } = state
    return !available
      ? {}
      : available.routePlans.reduce(
          (acc, cur) => ({ ...acc, [cur._id]: cur }),
          {}
        )
  },
  salesmen(state) {
    const { available } = state
    if (!available) return []
    return available.routePlans
      .map(route => route.salesman)
      .filter(
        (salesman, index, self) =>
          index === self.findIndex(item => item.code === salesman.code)
      )
      .sort((a, b) => {
        if (a.name > b.name) return 1
        if (a.name < b.name) return -1
        return 0
      })
  },
  allClientsBySalesman(state) {
    const { available } = state
    return employeeCode => {
      if (!available) return null
      const routes = available.routePlans.reduce(
        (accD, routePlan) => {
          if (routePlan.salesman.code !== employeeCode) return accD
          return routePlan.dailyRoutes.reduce((acc, cur) => {
            return cur.weekday === 0
              ? { ...acc, in: [...acc.in, ...cur.clients] }
              : { ...acc, out: [...acc.out, ...cur.clients] }
          }, accD)
        },
        {
          in: [],
          out: [],
        }
      )
      return [...routes.in, ...routes.out]
        .filter(
          (route, index, self) =>
            index === self.findIndex(item => item.code === route.code)
        )
        .sort((a, b) => {
          if (a.companyName > b.companyName) return 1
          if (a.companyName < b.companyName) return -1
          return 0
        })
    }
  },
  clientsByEmployee(state) {
    const { available } = state
    return payload => {
      const { coachee, weekday, salesmanCode } = payload
      const employeeCode = coachee.code
      const jobTitle = coachee.jobTitle
      if (!available) return null
      const generic = !employeeCode
      const routes = available.routePlans.reduce(
        (accD, routePlan) => {
          const employee = routePlan[jobTitle]
          if (
            !generic &&
            salesmanCode &&
            routePlan.salesman.code !== salesmanCode
          )
            return accD
          if (!generic && (!employee || employee.code !== employeeCode))
            return accD
          console.log(routePlan)
          return routePlan.dailyRoutes.reduce((acc, cur) => {
            const key = cur.weekday === weekday ? 'in' : 'out'
            return { ...acc, [key]: [...acc[key], ...cur.clients] }
          }, accD)
        },
        {
          in: [],
          out: [],
        }
      )
      return { hash: available.routeHash, ...routes }
    }
  },
  byWeekday(state) {
    const emptyAnswer = {
      0: [],
      1: [],
      2: [],
      3: [],
      4: [],
      5: [],
      6: [],
      7: [], // Clientes sem dia de visita
    }
    const reducerInitialValue = {
      0: {},
      1: {},
      2: {},
      3: {},
      4: {},
      5: {},
      6: {},
      7: {}, // Clientes sem dia de visita
    }
    const { available } = state
    if (!available) return emptyAnswer
    const routePlanReducer = (acc, cur) => {
      const { dailyRoutes, ...routePlan } = cur
      const { shortId } = routePlan
      return dailyRoutes.reduce((accD, dailyRoute) => {
        let { weekday } = dailyRoute
        weekday = weekday ?? 7
        let route = accD[weekday][shortId]
        if (!route) {
          route = {
            ...routePlan,
            weekday,
            clients: [],
          }
        }
        return {
          ...accD,
          [weekday]: {
            ...accD[weekday],
            [shortId]: {
              ...route,
              clients: [...route.clients, ...dailyRoute.clients],
            },
          },
        }
      }, acc)
    }
    const indexedRoutePlans =
      available.routePlans?.reduce(routePlanReducer, reducerInitialValue) ??
      reducerInitialValue
    return Object.values(indexedRoutePlans).reduce(
      (acc, cur, index) => ({ ...acc, [index]: Object.values(cur) }),
      emptyAnswer
    )
  },
  coachees(state, getters, rootState) {
    const { byWeekday } = getters
    const userId = rootState.auth?.user?.employeeCode
    const reducerInitialValue = { coordinators: {}, salesmen: {} }
    const indexedCoachees = Object.values(byWeekday).reduce(
      (accA, curA, index) =>
        curA.reduce((acc, cur) => {
          const coordinators = { ...acc[index].coordinators }
          const salesmen = { ...acc[index].salesmen }
          const { coordinator, salesman } = cur
          if (coordinator.code !== userId) {
            coordinators[coordinator.code] =
              coordinator.code in coordinators
                ? {
                    ...coordinators[coordinator.code],
                    clients: [
                      ...coordinators[coordinator.code].clients,
                      ...cur.clients,
                    ],
                  }
                : {
                    ...coordinator,
                    jobTitle: 'coordinator',
                    clients: cur.clients,
                  }
          }
          if (salesman.code !== userId && !(salesman.code in salesmen)) {
            salesmen[salesman.code] = {
              ...cur.salesman,
              jobTitle: 'salesman',
              clients: cur.clients,
            }
          }
          return {
            ...acc,
            [index]: { coordinators, salesmen },
          }
        }, accA),
      {
        0: reducerInitialValue,
        1: reducerInitialValue,
        2: reducerInitialValue,
        3: reducerInitialValue,
        4: reducerInitialValue,
        5: reducerInitialValue,
        6: reducerInitialValue,
        7: reducerInitialValue, // clientes sem dia de visita
      }
    )
    return Object.values(indexedCoachees).reduce(
      (acc, cur, index) => ({
        ...acc,
        [index]: {
          coordinators: Object.values(cur.coordinators),
          salesmen: Object.values(cur.salesmen),
        },
      }),
      {}
    )
  },
}

export const mutations = {
  UPDATE_ROOT: (state, payload) => Object.assign(state, payload),
}

export const actions = {
  async fetchAvailable({ commit, state, rootState, dispatch }, force) {
    if (!navigator.onLine) return false
    const { routePlan: offline } = rootState.offline
    if (offline.updating) return false
    if (offline.lastUpdate && !force) {
      const lastUpdate = new Date(offline.lastUpdate).getTime()
      const now = new Date().getTime()
      const diff = now - lastUpdate
      if (diff < 30000) return false
    }
    commit('offline/UPDATING', 'routePlan', { root: true })
    try {
      const response = await this.$axios.get('/v3/app/sync/routePlans')
      commit('UPDATE_ROOT', {
        available: {
          ...response.data,
        },
      })
      commit('offline/UPDATE', 'routePlan', { root: true })

      commit('coachingFeedback/CLEAR', 'routePlan', { root: true })
      dispatch('coachingFeedback/fetchCoachingFeedback', 'routePlan', {
        root: true,
      })
    } catch (e) {
      commit('offline/FAILED', 'routePlan', { root: true })
      $nuxt.$rollbar.error(e)
      return false
    }
    try {
      const { data } = await this.$axios.get('/v3/app/coachingDiagnosis')
      commit('UPDATE_ROOT', { coachingDiagnosis: data })
    } catch (error) {
      console.error('Erro ao buscar diagnósticos de coachings', error)
      $nuxt.$rollbar.error(error)
    }
    return true
  },
  useRoute({ commit, state }) {
    const { available } = state
    if (!state) throw new Error('No routePlan available to use')
    commit('UPDATE_ROOT', { hash: available.routeHash })
  },
  releaseRoute({ commit, state, dispatch }) {
    const { available } = state
    if (!state) throw new Error('No routePlan available to use')
    commit('UPDATE_ROOT', { hash: null })
    dispatch('fetchAvailable')
  },
}
